// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Body1, Spinner, Title3 } from '@fluentui/react-components';
import { FC, useEffect, useMemo, useState } from 'react';
import { renderApp } from '../../index';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { NetworkErrorMessage } from '../../libs/services/BaseService';
import { MaintenanceService, MaintenanceStatus } from '../../libs/services/MaintenanceService';
import { TestService } from '../../libs/services/TestService';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { setMaintenance } from '../../redux/features/app/appSlice';
import { useSharedClasses } from '../../styles';

interface IData {
    onBackendFound: () => void;
}

export const BackendProbe: FC<IData> = ({ onBackendFound }) => {
    const classes = useSharedClasses();
    const dispatch = useAppDispatch();
    const { isMaintenance } = useAppSelector((state: RootState) => state.app);
    const maintenanceService = useMemo(() => new MaintenanceService(), []);
    const { instance, inProgress } = useMsal();

    const [model, setModel] = useState<MaintenanceStatus | null>(null);

    useEffect(() => {
        let isDisposed = false;

        const onBackendFoundWithAuthCheck = async (): Promise<void> => {
            // If no auth config, bootstrap the app and stop here
            if (!AuthHelper.getAuthConfig()) {
                await renderApp();
                return;
            }

            // Signal that the backend is reachable
            onBackendFound();

            // Only run the test API calls in development
            if (process.env.NODE_ENV === 'development') {
                try {
                    const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
                    const testService = new TestService();

                    const whoami = await testService.getWhoAmIAsync(accessToken);
                    console.log('✅ /whoami response:', whoami);

                    const adminOnly = await testService.getAdminOnlyMessageAsync(accessToken);
                    console.log('✅ /adminonly response:', adminOnly.message);
                } catch (err) {
                    console.warn('❌ Test API calls failed:', err);
                }
            }
        };

        const tick = async (): Promise<void> => {
            try {
                const token = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

                try {
                    const data = await maintenanceService.getMaintenanceStatus(token);
                    if (!isDisposed) setModel(data);
                } catch (e: unknown) {
                    if (e instanceof Error && e.message.includes(NetworkErrorMessage)) {
                        // network error, try again next interval
                        return;
                    }

                    dispatch(setMaintenance(false));
                    await onBackendFoundWithAuthCheck();
                }
            } catch {
                // auth token failed—ignore and retry on next interval
            }
        };

        // Start polling
        const id = setInterval(() => {
            // Explicitly handle the returned Promise to avoid "floating promises"
            tick().catch((err) => {
                // Swallow errors to keep interval alive, but log for diagnostics
                console.warn('BackendProbe tick failed:', err);
            });
        }, 3000);

        return () => {
            isDisposed = true;
            clearInterval(id);
        };
    }, [dispatch, maintenanceService, onBackendFound, instance, inProgress]);

    return (
        <>
            {isMaintenance ? (
                <div className={classes.informativeView}>
                    <Title3>{model?.title ?? 'Site undergoing maintenance...'}</Title3>
                    <Spinner />
                    <Body1>
                        {model?.message ?? 'Planned site maintenance is underway.  We apologize for the disruption.'}
                    </Body1>
                    <Body1>
                        <strong>
                            {model?.note ??
                                "Note: If this message doesn't resolve after a significant duration, refresh the browser."}
                        </strong>
                    </Body1>
                </div>
            ) : (
                <div className={classes.informativeView}>
                    <Title3>Connecting...</Title3>
                    <Spinner />
                </div>
            )}
        </>
    );
};
